import React, { useEffect, useRef, useState } from "react";
import {
  fetchProducts,
  deleteProduct,
  saveProduct,
  updateProductPlacements,
  fetchAdminCategories,
  bulkUpdateProducts,
  bulkDeleteProducts,
  exportProductsCsv,
  importProductsCsv,
} from "../adminApi";
import { useNavigate } from "react-router-dom";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaClone,
  FaArrowLeft,
  FaTags,
} from "react-icons/fa";
import { getImageUrl } from "../../utils/imageUrl";

const PAGE_PLACEMENT_OPTIONS = [
  { key: "home", label: "Home" },
  { key: "wheatbag", label: "Wheatbag" },
  { key: "gift", label: "Gift" },
  { key: "kitchen", label: "Kitchen" },
  { key: "decor", label: "Decor" },
  { key: "handmade", label: "Handmade" },
];

const formatPlacementLabel = (key) =>
  PAGE_PLACEMENT_OPTIONS.find((option) => option.key === key)?.label || key;

const PRODUCT_CSV_TEMPLATE_HEADERS = [
  "product_id",
  "sku",
  "name_en",
  "name_ar",
  "name_tr",
  "name_ja",
  "category_id",
  "category_name",
  "price",
  "sale_price",
  "stock_quantity",
  "product_status",
  "product_slug",
  "model_code",
  "variation_type",
  "variation_value",
  "related_product_ids",
  "tags",
  "page_placements",
  "trendyol_link",
  "amazon_link",
  "amazon_japan_link",
  "hepsiburada_link",
  "meta_description",
  "meta_keywords",
  "image_urls",
  "images_json",
  "description_en",
  "description_ar",
  "description_tr",
  "description_ja",
  "createdAt",
  "updatedAt",
];

const normalizePlacements = (placements = []) =>
  [...placements]
    .map((placement, index) => ({
      id: placement.id,
      page_key: placement.page_key,
      sort_order: placement.sort_order ?? index,
      is_featured: Boolean(placement.is_featured),
    }))
    .sort((a, b) => a.sort_order - b.sort_order);

export default function ListProducts() {
  const csvInputRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [placementEditorProductId, setPlacementEditorProductId] =
    useState(null);
  const [placementDrafts, setPlacementDrafts] = useState({});
  const [savingPlacementsFor, setSavingPlacementsFor] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [bulkForm, setBulkForm] = useState({
    category_id: "",
    price: "",
    priceMode: "set",
    stock_quantity: "",
    description_en: "",
    description_ar: "",
    description_tr: "",
    description_ja: "",
    tags: "",
    tagMode: "add",
    page_placements: [],
    placementMode: "add",
    placementStartOrder: "0",
    placementFeatured: false,
    product_status: "",
  });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkError, setBulkError] = useState("");
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvMessage, setCsvMessage] = useState("");
  const [csvError, setCsvError] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // Filter by product status
  const navigate = useNavigate();

  const loadProducts = async () => {
    setLoading(true);
    const data = await fetchProducts();

    // ✅ Sort the images for each product after fetching
    const sortedProducts = data.map((product) => {
      if (product.images && product.images.length > 0) {
        // Create a new sorted array to avoid mutating the original data
        const sortedImages = [...product.images].sort(
          (a, b) => a.sort_order - b.sort_order,
        );
        return {
          ...product,
          images: sortedImages,
          pagePlacements: normalizePlacements(product.pagePlacements || []),
        };
      }
      return {
        ...product,
        pagePlacements: normalizePlacements(product.pagePlacements || []),
      };
    });

    setProducts(sortedProducts);
    setSelectedProductIds((prev) =>
      prev.filter((id) =>
        sortedProducts.some((product) => product.product_id === id),
      ),
    );
    setLoading(false);
  };

  useEffect(() => {
    const loadPageData = async () => {
      try {
        setLoading(true);
        const [productsResponse, categoriesResponse] = await Promise.all([
          fetchProducts(),
          fetchAdminCategories(),
        ]);

        const sortedProducts = productsResponse.map((product) => {
          if (product.images && product.images.length > 0) {
            const sortedImages = [...product.images].sort(
              (a, b) => a.sort_order - b.sort_order,
            );
            return {
              ...product,
              images: sortedImages,
              pagePlacements: normalizePlacements(product.pagePlacements || []),
            };
          }

          return {
            ...product,
            pagePlacements: normalizePlacements(product.pagePlacements || []),
          };
        });

        setProducts(sortedProducts);
        setCategories(categoriesResponse || []);
        setSelectedProductIds((prev) =>
          prev.filter((id) =>
            sortedProducts.some((product) => product.product_id === id),
          ),
        );
      } finally {
        setLoading(false);
      }
    };

    loadPageData();
  }, []);

  const selectedCount = selectedProductIds.length;
  const allProductsSelected =
    products.length > 0 && selectedCount === products.length;

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      await deleteProduct(id);
      loadProducts();
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
  };

  const handleSelectAllProducts = () => {
    setSelectedProductIds((prev) =>
      prev.length === products.length
        ? []
        : products.map((product) => product.product_id),
    );
  };

  const handleBulkFieldChange = (field, value) => {
    setBulkForm((prev) => ({ ...prev, [field]: value }));
  };

  const clearBulkForm = ({ clearStatus = true } = {}) => {
    setBulkForm({
      category_id: "",
      price: "",
      priceMode: "set",
      stock_quantity: "",
      description_en: "",
      description_ar: "",
      description_tr: "",
      description_ja: "",
      tags: "",
      tagMode: "add",
      page_placements: [],
      placementMode: "add",
      placementStartOrder: "0",
      placementFeatured: false,
      product_status: "",
    });

    if (clearStatus) {
      setBulkError("");
      setBulkMessage("");
    }
  };

  const handleBulkApply = async () => {
    const updates = {};

    if (bulkForm.category_id !== "") {
      updates.category_id = bulkForm.category_id;
    }
    if (bulkForm.price !== "") {
      updates.price = bulkForm.price;
      updates.priceMode = bulkForm.priceMode;
    }
    if (bulkForm.stock_quantity !== "") {
      updates.stock_quantity = bulkForm.stock_quantity;
    }

    [
      "description_en",
      "description_ar",
      "description_tr",
      "description_ja",
    ].forEach((field) => {
      if (bulkForm[field] !== "") {
        updates[field] = bulkForm[field];
      }
    });

    const normalizedTags = bulkForm.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);

    if (normalizedTags.length > 0) {
      updates.tags = normalizedTags;
      updates.tagMode = bulkForm.tagMode;
    }

    if (bulkForm.product_status && bulkForm.product_status !== "") {
      updates.product_status = bulkForm.product_status;
    }

    if (bulkForm.page_placements.length > 0) {
      const startOrder = Number(bulkForm.placementStartOrder) || 0;
      updates.pagePlacements = bulkForm.page_placements.map(
        (pageKey, index) => ({
          page_key: pageKey,
          sort_order: startOrder + index,
          is_featured: bulkForm.placementFeatured,
        }),
      );
      updates.placementMode = bulkForm.placementMode;
    }

    if (Object.keys(updates).length === 0) {
      setBulkError("Choose at least one field to update.");
      setBulkMessage("");
      return;
    }

    try {
      setBulkSaving(true);
      setBulkError("");
      setBulkMessage("");
      const response = await bulkUpdateProducts(selectedProductIds, updates);
      await loadProducts();
      setBulkMessage(
        `Updated ${response.updatedCount || selectedProductIds.length} products.`,
      );
      clearBulkForm({ clearStatus: false });
    } catch (error) {
      setBulkError(
        error.response?.data?.error || "Failed to apply bulk changes.",
      );
      setBulkMessage("");
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProductIds.length === 0) {
      setBulkError("Select at least one product to delete.");
      setBulkMessage("");
      return;
    }

    const confirmed = window.confirm(
      `Delete ${selectedProductIds.length} selected product(s)? Products linked to existing orders will be skipped automatically.`,
    );
    if (!confirmed) return;

    try {
      setBulkSaving(true);
      setBulkError("");
      setBulkMessage("");

      const response = await bulkDeleteProducts(selectedProductIds);
      await loadProducts();
      setSelectedProductIds([]);

      const blocked = response.blockedCount || 0;
      const missing = response.missingCount || 0;
      setBulkMessage(
        `Deleted ${response.deletedCount || 0} product(s). Skipped ${blocked} linked to orders${
          missing > 0 ? `, ${missing} missing` : ""
        }.`,
      );
    } catch (error) {
      setBulkError(
        error.response?.data?.error || "Failed to delete selected products.",
      );
      setBulkMessage("");
    } finally {
      setBulkSaving(false);
    }
  };

  const handleMakeVariant = (productId) => {
    const productToCopy = products.find((p) => p.product_id === productId);

    if (productToCopy) {
      // Navigate to the new product form, passing the product data in the state
      navigate("/dashboard-admin/products/new", {
        state: {
          product: productToCopy,
          isVariant: true,
        },
      });
    }
  };

  const handleBulkPlacementToggle = (pageKey) => {
    setBulkForm((prev) => ({
      ...prev,
      page_placements: prev.page_placements.includes(pageKey)
        ? prev.page_placements.filter((key) => key !== pageKey)
        : [...prev.page_placements, pageKey],
    }));
  };

  const handleDownloadCsvTemplate = () => {
    const csv = `\uFEFF${PRODUCT_CSV_TEMPLATE_HEADERS.join(",")}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "products_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    try {
      setCsvBusy(true);
      setCsvError("");
      setCsvMessage("");
      const csvBlob = await exportProductsCsv();
      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `products_export_${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setCsvMessage("Products CSV exported.");
    } catch (error) {
      setCsvError("Failed to export products CSV.");
    } finally {
      setCsvBusy(false);
    }
  };

  const handleImportCsvClick = () => {
    csvInputRef.current?.click();
  };

  const handleImportCsvSelected = async (event) => {
    const [file] = event.target.files || [];
    if (!file) return;

    try {
      setCsvBusy(true);
      setCsvError("");
      setCsvMessage("");
      const buffer = await file.arrayBuffer();
      const decoder = new TextDecoder("utf-8");
      const csvText = decoder.decode(buffer).replace(/^\uFEFF/, "");
      const response = await importProductsCsv(csvText);
      await loadProducts();
      setCsvMessage(response.message || "CSV import completed.");
    } catch (error) {
      setCsvError(
        error.response?.data?.error || "Failed to import products CSV.",
      );
    } finally {
      event.target.value = "";
      setCsvBusy(false);
    }
  };

  const handleInlineEdit = async (productId, field, value) => {
    try {
      const productToUpdate = products.find((p) => p.product_id === productId);
      if (!productToUpdate) return;

      const updatedProducts = products.map((p) =>
        p.product_id === productId ? { ...p, [field]: value } : p,
      );
      setProducts(updatedProducts);

      const updatedData = {
        ...productToUpdate,
        [field]: value,
        existingImages: productToUpdate.images,
      };

      await saveProduct(updatedData, productId);
    } catch (error) {
      console.error(`Failed to update product ${productId}:`, error);
      loadProducts();
    }
  };

  const openPlacementEditor = (product) => {
    setPlacementEditorProductId(product.product_id);
    setPlacementDrafts((prev) => ({
      ...prev,
      [product.product_id]: normalizePlacements(product.pagePlacements || []),
    }));
  };

  const closePlacementEditor = () => {
    setPlacementEditorProductId(null);
  };

  const handlePlacementToggle = (productId, pageKey) => {
    setPlacementDrafts((prev) => {
      const current = normalizePlacements(prev[productId] || []);
      const existing = current.find(
        (placement) => placement.page_key === pageKey,
      );

      if (existing) {
        return {
          ...prev,
          [productId]: current.filter(
            (placement) => placement.page_key !== pageKey,
          ),
        };
      }

      return {
        ...prev,
        [productId]: [
          ...current,
          {
            page_key: pageKey,
            sort_order: current.length,
            is_featured: false,
          },
        ],
      };
    });
  };

  const handlePlacementFieldChange = (productId, pageKey, field, value) => {
    setPlacementDrafts((prev) => ({
      ...prev,
      [productId]: (prev[productId] || []).map((placement) =>
        placement.page_key === pageKey
          ? {
              ...placement,
              [field]: field === "sort_order" ? Number(value) || 0 : value,
            }
          : placement,
      ),
    }));
  };

  const handleSavePlacements = async (productId) => {
    try {
      setSavingPlacementsFor(productId);
      const placementsToSave = normalizePlacements(
        placementDrafts[productId] || [],
      );
      const updatedProduct = await updateProductPlacements(
        productId,
        placementsToSave,
      );

      setProducts((prev) =>
        prev.map((product) =>
          product.product_id === productId
            ? {
                ...product,
                ...updatedProduct,
                pagePlacements: normalizePlacements(
                  updatedProduct.pagePlacements || [],
                ),
              }
            : product,
        ),
      );
      setPlacementEditorProductId(null);
    } catch (error) {
      console.error(
        `Failed to update placements for product ${productId}:`,
        error,
      );
      loadProducts();
    } finally {
      setSavingPlacementsFor(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-10 text-gray-500">Loading products...</div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/dashboard-admin")}
            className="text-gray-600 hover:text-gray-900 transition-colors"
            title="Back to Dashboard"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-3xl font-extrabold text-gray-900">
            Products List
          </h2>
        </div>
        <button
          onClick={() => navigate("/dashboard-admin/products/new")}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center shadow-md"
        >
          <FaPlus className="mr-2" /> Add Product
        </button>
      </div>

      <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Filters</h3>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="hidden">Hidden</option>
              <option value="discontinued">Discontinued</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setStatusFilter("")}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-slate-50 p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <h3 className="text-lg font-semibold text-slate-900">
              Catalog Import and Export
            </h3>
            <p className="text-sm text-slate-600">
              Export your current products to CSV, edit them in Excel or Google
              Sheets, then import the file back to create new rows or update
              existing ones by product ID or SKU.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              CSV now includes product metadata, rich descriptions, image URLs,
              and full image JSON details for round-trip editing.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleDownloadCsvTemplate}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Download Template
            </button>
            <button
              type="button"
              onClick={handleExportCsv}
              disabled={csvBusy}
              className="rounded-lg border border-blue-200 bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              {csvBusy ? "Working..." : "Export Products CSV"}
            </button>
            <button
              type="button"
              onClick={handleImportCsvClick}
              disabled={csvBusy}
              className="rounded-lg border border-emerald-200 bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
            >
              {csvBusy ? "Working..." : "Import CSV"}
            </button>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleImportCsvSelected}
              className="hidden"
            />
          </div>
        </div>

        {(csvError || csvMessage) && (
          <p
            className={`mt-4 text-sm ${
              csvError ? "text-red-600" : "text-emerald-600"
            }`}
          >
            {csvError || csvMessage}
          </p>
        )}
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Bulk Product Editor
            </h3>
            <p className="text-sm text-slate-600">
              Select products below, then update category, price, stock, tags,
              or page placement together.
            </p>
            <p className="mt-2 text-sm font-medium text-slate-700">
              {selectedCount > 0
                ? `${selectedCount} product${selectedCount === 1 ? "" : "s"} selected`
                : "No products selected yet"}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelectedProductIds([])}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-white"
            >
              Clear Selection
            </button>
            <button
              type="button"
              onClick={handleSelectAllProducts}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-white"
            >
              {allProductsSelected ? "Unselect All" : "Select All"}
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
          <select
            value={bulkForm.category_id}
            onChange={(e) =>
              handleBulkFieldChange("category_id", e.target.value)
            }
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
          >
            <option value="">Category: no change</option>
            {categories.map((category) => {
              const categoryId = category.category_id ?? category.id;
              if (!categoryId) return null;

              return (
                <option key={categoryId} value={categoryId}>
                  {category.name}
                </option>
              );
            })}
          </select>

          <input
            type="number"
            min="0"
            value={bulkForm.price}
            onChange={(e) => handleBulkFieldChange("price", e.target.value)}
            placeholder={
              bulkForm.priceMode === "set" ? "Set price" : "Percent amount"
            }
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
          />

          <select
            value={bulkForm.priceMode}
            onChange={(e) => handleBulkFieldChange("priceMode", e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
          >
            <option value="set">Set exact price</option>
            <option value="increase_percent">Increase by %</option>
            <option value="decrease_percent">Decrease by %</option>
          </select>

          <input
            type="number"
            min="0"
            value={bulkForm.stock_quantity}
            onChange={(e) =>
              handleBulkFieldChange("stock_quantity", e.target.value)
            }
            placeholder="Set stock"
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
          />

          <select
            value={bulkForm.product_status || ""}
            onChange={(e) =>
              handleBulkFieldChange("product_status", e.target.value)
            }
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
          >
            <option value="">Status: no change</option>
            <option value="active">Set to Active</option>
            <option value="draft">Set to Draft</option>
            <option value="hidden">Set to Hidden</option>
            <option value="discontinued">Set to Discontinued</option>
          </select>

          <input
            type="text"
            value={bulkForm.tags}
            onChange={(e) => handleBulkFieldChange("tags", e.target.value)}
            placeholder="Tags: lavender, gift"
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
          />

          <select
            value={bulkForm.tagMode}
            onChange={(e) => handleBulkFieldChange("tagMode", e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
          >
            <option value="add">Add tags</option>
            <option value="remove">Remove tags</option>
            <option value="replace">Replace tags</option>
          </select>
        </div>

        {/* <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
          <h4 className="text-sm font-semibold text-slate-900">
            Bulk Descriptions
          </h4>
          <p className="mt-1 text-xs text-slate-500">
            Fill any description field below to apply that text to all selected
            products. Leave blank to keep unchanged.
          </p>

          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <textarea
              value={bulkForm.description_en}
              onChange={(e) =>
                handleBulkFieldChange("description_en", e.target.value)
              }
              placeholder="Description EN"
              rows={3}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
            />
            <textarea
              value={bulkForm.description_ar}
              onChange={(e) =>
                handleBulkFieldChange("description_ar", e.target.value)
              }
              placeholder="Description AR"
              rows={3}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
            />
            <textarea
              value={bulkForm.description_tr}
              onChange={(e) =>
                handleBulkFieldChange("description_tr", e.target.value)
              }
              placeholder="Description TR"
              rows={3}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
            />
            <textarea
              value={bulkForm.description_ja}
              onChange={(e) =>
                handleBulkFieldChange("description_ja", e.target.value)
              }
              placeholder="Description JA"
              rows={3}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
            />
          </div>
        </div> */}

        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">
                Bulk Page Placement
              </h4>
              <p className="text-xs text-slate-500">
                Add, remove, or replace shared placements for all selected
                products.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:w-[34rem]">
              <select
                value={bulkForm.placementMode}
                onChange={(e) =>
                  handleBulkFieldChange("placementMode", e.target.value)
                }
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
              >
                <option value="add">Add placements</option>
                <option value="remove">Remove placements</option>
                <option value="replace">Replace placements</option>
              </select>

              <input
                type="number"
                min="0"
                value={bulkForm.placementStartOrder}
                onChange={(e) =>
                  handleBulkFieldChange("placementStartOrder", e.target.value)
                }
                placeholder="Start order"
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
              />

              <label className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  checked={bulkForm.placementFeatured}
                  onChange={(e) =>
                    handleBulkFieldChange("placementFeatured", e.target.checked)
                  }
                />
                Mark featured
              </label>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
            {PAGE_PLACEMENT_OPTIONS.map((option) => {
              const isChecked = bulkForm.page_placements.includes(option.key);
              return (
                <label
                  key={option.key}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${
                    isChecked
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleBulkPlacementToggle(option.key)}
                  />
                  {option.label}
                </label>
              );
            })}
          </div>
        </div>

        {(bulkError || bulkMessage) && (
          <p
            className={`mt-3 text-sm ${
              bulkError ? "text-red-600" : "text-emerald-600"
            }`}
          >
            {bulkError || bulkMessage}
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleBulkApply}
            disabled={selectedCount === 0 || bulkSaving}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {bulkSaving ? "Applying..." : "Apply to Selected"}
          </button>
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={selectedCount === 0 || bulkSaving}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
          >
            {bulkSaving ? "Working..." : "Delete Selected"}
          </button>
          <button
            type="button"
            onClick={clearBulkForm}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-white"
          >
            Reset Fields
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg shadow-xl border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={allProductsSelected}
                  onChange={handleSelectAllProducts}
                  aria-label="Select all products"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Product Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Stock
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Images
              </th>
              {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tags
              </th> */}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Page Placement
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products
              .filter(
                (prod) => !statusFilter || prod.product_status === statusFilter,
              )
              .map((prod, index) => {
                const productPublicPath = `/product/${
                  prod.product_slug || prod.product_id
                }`;
                const draftPlacements =
                  placementDrafts[prod.product_id] || prod.pagePlacements || [];
                const isPlacementEditorOpen =
                  placementEditorProductId === prod.product_id;

                return (
                  <React.Fragment key={prod.product_id}>
                    <tr
                      className={
                        index % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50 hover:bg-gray-100"
                      }
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        <input
                          type="checkbox"
                          checked={selectedProductIds.includes(prod.product_id)}
                          onChange={() => handleSelectProduct(prod.product_id)}
                          aria-label={`Select product ${prod.name_en}`}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {prod.product_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-semibold">
                        <button
                          type="button"
                          onClick={() => navigate(productPublicPath)}
                          className="text-left text-blue-700 hover:text-blue-900 hover:underline"
                          title="Open product page"
                        >
                          {prod.name_en}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {prod.Category?.name || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <input
                          type="number"
                          value={prod.price}
                          onChange={(e) =>
                            handleInlineEdit(
                              prod.product_id,
                              "price",
                              e.target.value,
                            )
                          }
                          className="w-24 text-center bg-blue-50 border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <input
                          type="number"
                          value={prod.stock_quantity}
                          onChange={(e) =>
                            handleInlineEdit(
                              prod.product_id,
                              "stock_quantity",
                              e.target.value,
                            )
                          }
                          className="w-24 text-center bg-blue-50 border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex -space-x-2 overflow-hidden">
                          {prod.images && prod.images.length > 0 ? (
                            prod.images.slice(0, 3).map((img) => (
                              <button
                                key={img.id}
                                type="button"
                                onClick={() => navigate(productPublicPath)}
                                className="inline-block rounded-full ring-2 ring-white overflow-hidden focus:outline-none focus:ring-blue-500"
                                title="Open product page"
                              >
                                <img
                                  className="h-12 w-12 object-cover"
                                  src={getImageUrl(img.image_url)}
                                  alt="Product"
                                />
                              </button>
                            ))
                          ) : (
                            <span className="text-gray-400">No images</span>
                          )}
                          {prod.images && prod.images.length > 3 && (
                            <div className="h-12 w-12 rounded-full ring-2 ring-white bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-500">
                              +{prod.images.length - 3}
                            </div>
                          )}
                        </div>
                      </td>
                      {/* <td className="px-6 py-4 text-sm text-gray-500 min-w-[200px]">
                        <div className="flex flex-wrap gap-2">
                          {Array.isArray(prod.tags) && prod.tags.length > 0 ? (
                            prod.tags.map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400">No tags</span>
                          )}
                        </div>
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <select
                          value={prod.product_status || "active"}
                          onChange={(e) =>
                            handleInlineEdit(
                              prod.product_id,
                              "product_status",
                              e.target.value,
                            )
                          }
                          className="rounded-md border-gray-300 bg-white px-3 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="draft">Draft</option>
                          <option value="active">Active</option>
                          <option value="hidden">Hidden</option>
                          <option value="discontinued">Discontinued</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 min-w-[240px]">
                        <div className="flex flex-wrap gap-2">
                          {prod.pagePlacements?.length ? (
                            prod.pagePlacements.map((placement) => (
                              <span
                                key={placement.page_key}
                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                                  placement.is_featured
                                    ? "bg-amber-100 text-amber-800"
                                    : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {formatPlacementLabel(placement.page_key)}
                                {placement.is_featured
                                  ? "Featured"
                                  : `#${placement.sort_order}`}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400">
                              No page placements
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() =>
                              isPlacementEditorOpen
                                ? closePlacementEditor()
                                : openPlacementEditor(prod)
                            }
                            className="text-violet-600 hover:text-violet-900"
                            title="Manage Placements"
                          >
                            <FaTags className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() =>
                              navigate(
                                `/dashboard-admin/products/edit/${prod.product_id}`,
                              )
                            }
                            className="text-blue-600 hover:text-blue-900"
                            title="Full Edit"
                          >
                            <FaEdit className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleMakeVariant(prod.product_id)}
                            className="text-gray-600 hover:text-gray-900"
                            title="Make Variant"
                          >
                            <FaClone className="h-5 w-5" />
                          </button>

                          <button
                            onClick={() => handleDelete(prod.product_id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Product"
                          >
                            <FaTrash className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isPlacementEditorOpen && (
                      <tr className="bg-violet-50/40">
                        <td colSpan="10" className="px-6 py-5">
                          <div className="rounded-xl border border-violet-100 bg-white p-4 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h3 className="text-sm font-semibold text-gray-900">
                                  Page Placement Manager
                                </h3>
                                <p className="text-xs text-gray-500">
                                  Control where this product appears, featured
                                  state, and section order.
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                              {PAGE_PLACEMENT_OPTIONS.map((option) => {
                                const isChecked = draftPlacements.some(
                                  (placement) =>
                                    placement.page_key === option.key,
                                );
                                return (
                                  <label
                                    key={option.key}
                                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer ${
                                      isChecked
                                        ? "border-violet-400 bg-violet-50"
                                        : "border-gray-200 bg-white"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() =>
                                        handlePlacementToggle(
                                          prod.product_id,
                                          option.key,
                                        )
                                      }
                                    />
                                    <span className="text-sm text-gray-700">
                                      {option.label}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>

                            {draftPlacements.length > 0 && (
                              <div className="space-y-3">
                                {normalizePlacements(draftPlacements).map(
                                  (placement) => (
                                    <div
                                      key={placement.page_key}
                                      className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center rounded-lg border border-gray-200 p-3"
                                    >
                                      <div className="font-medium text-sm text-gray-900">
                                        {formatPlacementLabel(
                                          placement.page_key,
                                        )}
                                      </div>
                                      <label className="flex items-center gap-2 text-sm text-gray-600">
                                        <span>Order</span>
                                        <input
                                          type="number"
                                          min="0"
                                          value={placement.sort_order}
                                          onChange={(e) =>
                                            handlePlacementFieldChange(
                                              prod.product_id,
                                              placement.page_key,
                                              "sort_order",
                                              e.target.value,
                                            )
                                          }
                                          className="w-24 rounded border border-gray-300 px-2 py-1"
                                        />
                                      </label>
                                      <label className="flex items-center gap-2 text-sm text-gray-600">
                                        <input
                                          type="checkbox"
                                          checked={placement.is_featured}
                                          onChange={(e) =>
                                            handlePlacementFieldChange(
                                              prod.product_id,
                                              placement.page_key,
                                              "is_featured",
                                              e.target.checked,
                                            )
                                          }
                                        />
                                        <span>Featured in this section</span>
                                      </label>
                                    </div>
                                  ),
                                )}
                              </div>
                            )}

                            <div className="mt-4 flex justify-end gap-3">
                              <button
                                type="button"
                                onClick={closePlacementEditor}
                                className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleSavePlacements(prod.product_id)
                                }
                                disabled={
                                  savingPlacementsFor === prod.product_id
                                }
                                className="px-4 py-2 text-sm rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60"
                              >
                                {savingPlacementsFor === prod.product_id
                                  ? "Saving..."
                                  : "Save placements"}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
